/**
 * COMPTABILITÉ — LA VISIONNEUSE DE SOURCES.
 *
 * Partout dans la Comptabilité, une source se CLIQUE : le PDF s'ouvre en
 * grand, par-dessus l'écran, sans le quitter. On voit la pièce elle-même — pas
 * sa lecture, pas un résumé — et on revient exactement où on était.
 *
 * ═══ CE QUI S'AFFICHE ═══
 *
 * Le fichier archivé, servi par `GET /api/v1/finance/sources/:id/file`, dans
 * le lecteur PDF du navigateur (zoom, recherche, impression). La route est la
 * SEULE à accepter d'être encadrée, et seulement par Gestion elle-même.
 *
 * Quand on l'ouvre depuis une liste (les rapports d'une nuit), ← et → passent
 * d'une pièce à l'autre : on compare un complément à son principal sans
 * refermer.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Download, ExternalLink, FileText, Loader2, X } from 'lucide-react'
import { Link } from '../router.js'
import { Modal } from '../admin/primitives.js'
import { SOURCE_PROVIDER_LABELS, heureParis, nuitCourte } from './ClosingCommon.js'
import './finance-viewer.css'

/** Ce qu'il faut savoir d'un document pour l'afficher — rien de plus. */
export interface DocVisible {
  id: string
  filename: string
  provider?: string | null
  report_type?: string | null
  business_date?: string | null
  received_at?: string | null
  /** Un libellé métier déjà connu de l'écran appelant (« Rapport principal »…). */
  libelle?: string | null
}

const TYPES: Record<string, string> = {
  ZAP_REGISTER_SUMMARY: 'Rapport de caisse ZAP',
  ZAP_OTHER: 'Annexe ZAP',
  SHOTGUN_EVENT_REPORT: 'Reddition Shotgun',
  INCONNU: 'Document',
}

export const urlFichierSource = (id: string): string => `/api/v1/finance/sources/${id}/file`

/**
 * Le déclencheur : un bouton qui ressemble à un lien. `liste` permet de
 * parcourir les pièces voisines sans refermer.
 */
export function VoirSource(props: {
  doc: DocVisible
  liste?: DocVisible[]
  className?: string
  children?: ReactNode
  /** Libellé accessible quand l'enfant n'est pas un texte. */
  ariaLabel?: string
}) {
  const [ouvert, setOuvert] = useState(false)
  const liste = props.liste && props.liste.some((d) => d.id === props.doc.id) ? props.liste : [props.doc]
  return (
    <>
      <button
        type="button"
        className={props.className ?? 'fv-lien'}
        onClick={() => setOuvert(true)}
        aria-label={props.ariaLabel ?? `Voir le PDF ${props.doc.filename}`}
        title="Voir le PDF"
      >
        {props.children ?? props.doc.filename}
      </button>
      {ouvert ? (
        <Visionneuse docs={liste} depart={liste.findIndex((d) => d.id === props.doc.id)} onClose={() => setOuvert(false)} />
      ) : null}
    </>
  )
}

export function Visionneuse(props: { docs: DocVisible[]; depart: number; onClose: () => void }) {
  const [rang, setRang] = useState(Math.max(0, props.depart))
  const [charge, setCharge] = useState(false)
  const doc = props.docs[rang] ?? props.docs[0]!
  const n = props.docs.length

  // Une autre pièce = un autre chargement : l'indicateur repart.
  useEffect(() => { setCharge(false) }, [doc.id])

  useEffect(() => {
    if (n < 2) return
    const touche = (e: KeyboardEvent) => {
      const cible = e.target as HTMLElement | null
      if (cible && (cible.tagName === 'INPUT' || cible.tagName === 'TEXTAREA')) return
      if (e.key === 'ArrowRight') setRang((r) => (r + 1) % n)
      if (e.key === 'ArrowLeft') setRang((r) => (r - 1 + n) % n)
    }
    document.addEventListener('keydown', touche)
    return () => document.removeEventListener('keydown', touche)
  }, [n])

  const meta = [
    doc.libelle ?? (doc.report_type ? TYPES[doc.report_type] ?? doc.report_type : null),
    doc.provider ? SOURCE_PROVIDER_LABELS[doc.provider as keyof typeof SOURCE_PROVIDER_LABELS] ?? doc.provider : null,
    doc.business_date ? `nuit du ${nuitCourte(doc.business_date)}` : null,
    doc.received_at ? `reçu à ${heureParis(doc.received_at)}` : null,
  ].filter((x): x is string => !!x)

  return (
    <Modal open onClose={props.onClose} owning ariaLabel={`Document source : ${doc.filename}`} className="fv-modal">
      <header className="lc-modal__head fv-tete">
        <span className="lc-modal__icon" aria-hidden="true"><FileText size={22} /></span>
        <div className="fv-tete__texte">
          <div className="lc-modal__title fv-tete__titre" title={doc.filename}>{doc.filename}</div>
          {meta.length > 0 ? <p className="fv-tete__meta">{meta.join(' · ')}</p> : null}
        </div>
        <div className="fv-tete__actions">
          {n > 1 ? (
            <div className="fv-nav" role="group" aria-label="Parcourir les pièces">
              <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" onClick={() => setRang((r) => (r - 1 + n) % n)} aria-label="Pièce précédente">
                <ChevronLeft size={18} />
              </button>
              <span className="fv-nav__rang">{rang + 1} / {n}</span>
              <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" onClick={() => setRang((r) => (r + 1) % n)} aria-label="Pièce suivante">
                <ChevronRight size={18} />
              </button>
            </div>
          ) : null}
          <Link className="lc-btn lc-btn--ghost lc-btn--sm" href={`/comptabilite/sources/${doc.id}`}>Fiche du document</Link>
          <a className="lc-btn lc-btn--ghost lc-btn--icon" href={urlFichierSource(doc.id)} target="_blank" rel="noopener" aria-label="Ouvrir dans un nouvel onglet" title="Nouvel onglet">
            <ExternalLink size={17} />
          </a>
          <a className="lc-btn lc-btn--ghost lc-btn--icon" href={urlFichierSource(doc.id)} download={doc.filename} aria-label="Télécharger" title="Télécharger">
            <Download size={17} />
          </a>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" onClick={props.onClose} aria-label="Fermer">
            <X size={18} />
          </button>
        </div>
      </header>
      <div className="fv-corps">
        {!charge ? (
          <p className="fv-chargement" role="status"><Loader2 size={18} aria-hidden="true" /> Ouverture du document en cours</p>
        ) : null}
        <iframe
          key={doc.id}
          className={`fv-cadre${charge ? ' fv-cadre--pret' : ''}`}
          src={`${urlFichierSource(doc.id)}#view=FitH`}
          title={`Aperçu de ${doc.filename}`}
          onLoad={() => setCharge(true)}
        />
      </div>
    </Modal>
  )
}
